import React, { useState } from "react";
import { useApp } from "../../contexts/AppContext";

export const ApproverSelector = ({ onSelect, onCancel, loading }) => {
  const { service } = useApp();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedApprovers, setSelectedApprovers] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);

  const handleSearch = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    setSearching(true);
    try {
      const results = await service.searchUsers(query);
      const users = (results?.value || []).map(u => ({
        id: u.id,
        displayName: u.displayName,
        mail: u.mail,
        jobTitle: u.jobTitle
      }));
      setSearchResults(users);
    } catch (e) {
      console.error("Search error:", e);
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const addApprover = (user) => {
    if (!selectedApprovers.find(a => a.mail === user.mail)) {
      setSelectedApprovers([...selectedApprovers, user]);
    }
    setSearchQuery("");
    setSearchResults([]);
    setShowDropdown(false);
  };

  const removeApprover = (email) => {
    setSelectedApprovers(selectedApprovers.filter(a => a.mail !== email));
  };

  const moveApprover = (index, direction) => {
    const newApprovers = [...selectedApprovers];
    if (direction === "up" && index > 0) {
      [newApprovers[index], newApprovers[index - 1]] = [newApprovers[index - 1], newApprovers[index]];
    } else if (direction === "down" && index < newApprovers.length - 1) {
      [newApprovers[index], newApprovers[index + 1]] = [newApprovers[index + 1], newApprovers[index]];
    }
    setSelectedApprovers(newApprovers);
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-5">
      <div className="bg-light-bg-secondary dark:bg-dark-bg-secondary border border-light-border dark:border-dark-border rounded-2xl p-8 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <h2 className="font-serif text-2xl font-medium text-light-text dark:text-dark-text mb-2">Select Approvers</h2>
        <p className="text-light-text-secondary dark:text-dark-text-secondary mb-6">Add approvers in the order they should sign. They will be notified sequentially.</p>

        {/* Search */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-light-text-secondary dark:text-dark-text-secondary mb-3">Search and Add Approvers:</label>
          <div className="relative">
            <input
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setShowDropdown(true);
                handleSearch(e.target.value);
              }}
              onFocus={() => setShowDropdown(true)}
              placeholder="Search by name or email..."
              className="w-full px-4 py-3 bg-light-bg-tertiary dark:bg-dark-bg-tertiary border border-light-border dark:border-dark-border rounded-lg text-light-text dark:text-dark-text text-sm outline-none focus:ring-2 focus:ring-blue-600"
            />
            {showDropdown && (searchResults.length > 0 || searchQuery.trim()) && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-light-bg-secondary dark:bg-dark-bg-secondary border border-light-border dark:border-dark-border rounded-lg shadow-lg z-50 max-h-48 overflow-y-auto">
                {searching && <div className="p-3 text-light-text-secondary dark:text-dark-text-secondary text-sm">Searching...</div>}
                {!searching && searchResults.length > 0 && (
                  <>
                    {searchResults.map((user, i) => (
                      <button
                        key={i}
                        onClick={() => addApprover(user)}
                        className="w-full text-left px-4 py-3 hover:bg-light-bg-tertiary dark:hover:bg-dark-bg-tertiary border-b border-light-border dark:border-dark-border last:border-b-0 transition-colors"
                      >
                        <div className="text-light-text dark:text-dark-text text-sm font-medium">{user.displayName}</div>
                        <div className="text-light-text-secondary dark:text-dark-text-secondary text-xs">{user.mail}</div>
                      </button>
                    ))}
                  </>
                )}
                {!searching && searchResults.length === 0 && searchQuery.trim() && (
                  <div className="p-3 text-light-text-secondary dark:text-dark-text-secondary text-sm">No users found</div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Selected Approvers */}
        {selectedApprovers.length > 0 && (
          <div className="mb-6">
            <label className="block text-sm font-medium text-light-text-secondary dark:text-dark-text-secondary mb-3">Approvers in Order ({selectedApprovers.length}):</label>
            <div className="space-y-2">
              {selectedApprovers.map((approver, index) => (
                <div key={index} className="flex items-center gap-3 p-3 bg-light-bg-tertiary dark:bg-dark-bg-tertiary border border-light-border dark:border-dark-border rounded-lg">
                  <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white text-sm font-bold">
                    {index + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-light-text dark:text-dark-text text-sm font-medium truncate">{approver.displayName}</div>
                    <div className="text-light-text-secondary dark:text-dark-text-secondary text-xs truncate">{approver.mail}</div>
                  </div>
                  <div className="flex gap-1">
                    <button
                      onClick={() => moveApprover(index, "up")}
                      disabled={index === 0}
                      className="px-2 py-1 bg-light-bg-secondary dark:bg-dark-bg-secondary border border-light-border dark:border-dark-border rounded text-sm hover:bg-light-border dark:hover:bg-dark-border disabled:opacity-50"
                    >
                      ↑
                    </button>
                    <button
                      onClick={() => moveApprover(index, "down")}
                      disabled={index === selectedApprovers.length - 1}
                      className="px-2 py-1 bg-light-bg-secondary dark:bg-dark-bg-secondary border border-light-border dark:border-dark-border rounded text-sm hover:bg-light-border dark:hover:bg-dark-border disabled:opacity-50"
                    >
                      ↓
                    </button>
                    <button
                      onClick={() => removeApprover(approver.mail)}
                      className="px-2 py-1 text-red-500 hover:bg-red-500/10 rounded text-sm"
                    >
                      ✕
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Buttons */}
        <div className="flex gap-3 justify-end">
          <button
            onClick={onCancel}
            disabled={loading}
            className="px-6 py-2.5 bg-transparent border border-light-border dark:border-dark-border rounded-lg text-light-text-secondary dark:text-dark-text-secondary cursor-pointer text-sm hover:bg-light-bg-tertiary dark:hover:bg-dark-bg-tertiary transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={() => onSelect(selectedApprovers)}
            disabled={loading || selectedApprovers.length === 0}
            className={`px-6 py-2.5 rounded-lg font-semibold text-sm transition-all ${
              loading || selectedApprovers.length === 0
                ? "bg-gray-200 text-gray-700 cursor-not-allowed"
                : "bg-blue-600 hover:bg-blue-700 text-white cursor-pointer"
            }`}
          >
            {loading ? "Processing..." : `Proceed with ${selectedApprovers.length} Approver${selectedApprovers.length !== 1 ? "s" : ""}`}
          </button>
        </div>
      </div>
    </div>
  );
};
